import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { BookCallButton } from '../components/BookCallButton';
export function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <article className="py-12 md:py-20 px-6 md:px-12 max-w-3xl mx-auto" data-id="element-256">
      <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-12 text-sm" data-id="element-257">
        <Link to="/" className="inline-flex items-center gap-1.5 text-ink/60 hover:text-teal transition-colors" data-id="element-258">
          <ArrowLeftIcon className="w-3.5 h-3.5" data-id="element-259" />
          <span data-id="element-260">Home</span>
        </Link>
        <span className="text-ink/30" data-id="element-261">/</span>
        <a href="#story" className="text-teal font-medium hover:text-teal/70 transition-colors" data-id="element-262">
          My Story
        </a>
        <span className="text-ink/30" data-id="element-263">·</span>
        <a href="#philosophy" className="text-teal font-medium hover:text-teal/70 transition-colors" data-id="element-264">
          The Philosophy
        </a>
      </nav>

      <p id="story" className="text-xs uppercase tracking-widest text-accent mb-4 font-medium scroll-mt-8" data-id="element-265">
        Why I started Year One
      </p>
      <h1 className="text-5xl md:text-6xl font-serif leading-[1.05] mb-12" data-id="element-266">
        One conversation can change everything
        <span className="text-accent" data-id="element-267">.</span>
      </h1>

      <div className="flex items-center gap-5 mb-12 pb-12 border-b border-ink/10" data-id="element-268">
        <img src="https://cdn.magicpatterns.com/uploads/rHY1f3YTu8xTAzKyKufzyh/profile-larissa.jpg" alt="Larissa, founder of Year One Mentor" className="w-16 h-16 rounded-full object-cover" data-id="element-269" />
        <div data-id="element-270">
          <p className="font-serif text-xl text-ink" data-id="element-271">Larissa</p>
          <p className="text-sm text-ink/60" data-id="element-272">Founder, Year One Mentor</p>
        </div>
      </div>

      <div className="space-y-6 text-lg text-ink/80 leading-relaxed font-light" data-id="element-273">
        <p className="font-serif text-2xl md:text-3xl text-ink italic leading-snug" data-id="element-274">
          I was the underdog from the start.
        </p>
        <p data-id="element-275">
          I flunked my first year of college and was told I would probably end
          up working at McDonald's. I was not the obvious success story. I had
          to figure things out the hard way.
        </p>
        <p data-id="element-276">
          While working retail, I met a customer who worked at NBC. I got
          curious about her job, she offered me a tour of the lot, and I
          actually followed up.
        </p>
        <p className="font-serif text-xl md:text-2xl text-teal leading-snug py-2" data-id="element-277">
          That relationship led to my first opportunity in tech.
        </p>
        <p data-id="element-278">
          What started as a way to help pay for college ended up launching my
          career. I went from selling shoes to working in Silicon Valley during
          the dot-com boom alongside founders, executives, and some of the top
          minds in tech.
        </p>
        <p data-id="element-279">
          Over the years, I helped build products across consumer web, mobile,
          social, and now AI at companies like NBC, Yahoo, Friendster, and
          Evite.
        </p>
        <p data-id="element-280">
          I was often the square peg in the room. A creative in a technical
          world. I did not come from the traditional Silicon Valley pipeline or
          pedigree. What carried me was grit, curiosity, empathy, and the
          ability to connect with people.
        </p>
        <p data-id="element-281">
          Looking back, I realize my career was shaped as much by relationships,
          mentorship, and people who believed in me as it was by talent or
          skill.
        </p>
        <p data-id="element-282">
          As I stepped away from big tech and became a mother, my definition of
          success began to change. I became more interested in meaningful work,
          mentorship, and helping others find their way.
        </p>
        <p data-id="element-283">
          That led me to Arizona State University, where I helped develop and
          pilot an AI-assisted career exploration and coaching program designed
          to support students and recent graduates navigating an increasingly
          uncertain job market.
        </p>
        <p data-id="element-284">
          I saw the same thing over and over again: smart, capable young people
          with degrees, but no roadmap. They were overwhelmed by what they saw
          on LinkedIn, hearing conflicting advice, collecting credentials, and
          still unsure how to actually build a career or define success for
          themselves.
        </p>
        <p data-id="element-285">
          Career services often teach students how to apply for jobs. What many
          grads really need is confidence, personalized guidance, real-world
          perspective, and support from someone who has actually built teams,
          hired people, and navigated the industry firsthand.
        </p>
        <p className="font-serif text-2xl md:text-3xl text-ink leading-snug py-2" data-id="element-286">
          That is why I started Year One Mentor
          <span className="text-accent" data-id="element-287">.</span>
        </p>
        <p data-id="element-288">
          Year One Mentor is not just resume help or job search coaching. It is
          designed to help emerging talent build the foundation they need to
          launch a meaningful career and life.
        </p>
        <p data-id="element-289">
          I help grads figure out where they fit, how to tell their story, how
          to build relationships, gain experience, and develop the confidence
          and resilience needed to navigate the real world.
        </p>
        <p data-id="element-290">
          Because sometimes one conversation, one relationship, or one person
          believing in you can change your entire trajectory.
        </p>
        <p className="font-serif text-xl md:text-2xl text-teal italic leading-snug pt-2" data-id="element-291">
          I know. It changed mine.
        </p>
      </div>

      {/* Why "Year One" feature section */}
      <section id="philosophy" className="mt-20 -mx-6 md:-mx-12 scroll-mt-8" data-id="element-292">
        <div className="bg-mist/50 px-6 md:px-12 py-14 md:py-20 rounded-3xl" data-id="element-293">
          <div className="max-w-3xl mx-auto" data-id="element-294">
            <p className="text-xs uppercase tracking-widest text-accent mb-4 font-medium text-center" data-id="element-295">
              The Philosophy
            </p>
            <h2 className="text-4xl md:text-5xl font-serif leading-[1.1] mb-10 text-center" data-id="element-296">
              Why "Year One"<span className="text-accent" data-id="element-297">.</span>
            </h2>

            <div className="rounded-2xl overflow-hidden bg-white mb-10 shadow-sm max-w-[80%] mx-auto" data-id="element-298">
              <img src="https://cdn.magicpatterns.com/uploads/4KfhmgkQqz64Yc7q27ChQR/ChatGPT_Image_May_27,_2026,_01_20_20_PM.png" alt="An illustration of a mentor teaching a young person to fish by a lake, captioned: Give a man a fish, and you feed him for a day; teach a man to fish, and you feed him for a lifetime." className="w-full h-auto" data-id="element-299" />
            </div>

            <div className="space-y-6 text-lg text-ink/80 leading-relaxed font-light" data-id="element-300">
              <p data-id="element-301">
                That first year after graduation is foundational. It's when
                young adults start building confidence, gaining experience,
                forming professional habits, and learning how the real world
                actually works. But many grads enter that phase without enough
                direction, support, or real-world exposure, and once they lose
                momentum, it can become harder to build experience and a clear
                path forward.
              </p>
              <p data-id="element-302">
                Year One is designed as a kind of{' '}
                <em className="italic text-ink" data-id="element-303">real-world finishing school</em>
                , helping young adults get started intentionally. My role is to
                help them explore career paths, gain experience, build stronger
                people skills, and learn how to navigate opportunities beyond
                simply applying online.
              </p>
              <p data-id="element-304">
                I provide guidance, structure, accountability, and the
                occasional push when needed, with the goal of setting them up
                for life, not making them dependent on support.
              </p>
              <p className="font-serif text-xl md:text-2xl text-teal leading-snug py-2" data-id="element-305">
                I'm a big believer in teaching people{' '}
                <span className="italic" data-id="element-306">how to fish</span> — how to think,
                adapt, build relationships, and move forward on their own with
                confidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="mt-16 text-center" data-id="element-307">
        <BookCallButton className="inline-flex items-center justify-center px-6 py-3 bg-teal text-white font-medium rounded-md hover:bg-teal/90 transition-colors duration-200" data-id="element-308" />
      </div>
    </article>;
}